import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Plus, ChevronLeft, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';
import {
  PRACTICE_KINDS,
  isPracticeKindId,
  type PracticeKindId,
} from '@/practice/registry';
import AddPracticeItemDialog from '@/components/practice/AddPracticeItemDialog';

interface Lesson {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  is_published: boolean;
}

interface CourseInfo {
  id: string;
  name: string;
  org_id: string;
}

interface PracticeItem {
  id: string;
  kind_id: string;
  test_id: string | null;
  worksheet_id: string | null;
  game_id: string | null;
  writing_prompt_id: string | null;
  position: number;
  /** Joined display label (best-effort; falls back to id). */
  label?: string;
}

export default function OrgLessonEdit() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { canManage } = useCurrentOrg();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [course, setCourse] = useState<CourseInfo | null>(null);
  const [items, setItems] = useState<PracticeItem[]>([]);
  const [enabledKinds, setEnabledKinds] = useState<PracticeKindId[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [titleEdit, setTitleEdit] = useState('');
  const [descEdit, setDescEdit] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const load = async () => {
    if (!lessonId) return;
    setLoading(true);

    const { data: l, error: lErr } = await supabase
      .from('course_lessons')
      .select('id, course_id, title, description, is_published')
      .eq('id', lessonId)
      .is('deleted_at', null)
      .maybeSingle();

    if (lErr || !l) {
      toast.error('Lesson not found');
      navigate('/org/courses');
      return;
    }
    setLesson(l);
    setTitleEdit(l.title);
    setDescEdit(l.description ?? '');

    const { data: c } = await supabase
      .from('courses')
      .select('id, name, org_id')
      .eq('id', l.course_id)
      .maybeSingle();
    setCourse(c ?? null);

    if (c) {
      const { data: settings } = await supabase
        .from('org_practice_settings')
        .select('kind_id, enabled')
        .eq('org_id', c.org_id);
      const kinds = (settings ?? [])
        .filter((s: any) => s.enabled && isPracticeKindId(s.kind_id))
        .map((s: any) => s.kind_id as PracticeKindId);
      setEnabledKinds(kinds);
    }

    const { data: rows } = await supabase
      .from('lesson_practice_items')
      .select('id, kind_id, test_id, worksheet_id, game_id, writing_prompt_id, position')
      .eq('lesson_id', lessonId)
      .order('position', { ascending: true });

    const list = rows ?? [];
    // Resolve labels in parallel.
    const testIds = list.filter((r: any) => r.test_id).map((r: any) => r.test_id);
    const gameIds = list.filter((r: any) => r.game_id).map((r: any) => r.game_id);
    const [tests, games] = await Promise.all([
      testIds.length
        ? supabase.from('instructor_tests').select('id, title').in('id', testIds)
        : Promise.resolve({ data: [] as any[] }),
      gameIds.length
        ? supabase.from('games').select('id, name').in('id', gameIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const testMap = new Map((tests.data ?? []).map((t: any) => [t.id, t.title]));
    const gameMap = new Map((games.data ?? []).map((g: any) => [g.id, g.name]));

    setItems(
      list.map((r: any) => ({
        ...r,
        label:
          (r.test_id && testMap.get(r.test_id)) ||
          (r.game_id && gameMap.get(r.game_id)) ||
          (r.kind_id === 'writing_prompt' ? 'Writing prompt' : 'Worksheet') ||
          undefined,
      })),
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const togglePublish = async (next: boolean) => {
    if (!lesson) return;
    setLesson({ ...lesson, is_published: next });
    const { error } = await supabase
      .from('course_lessons')
      .update({ is_published: next })
      .eq('id', lesson.id);
    if (error) {
      setLesson({ ...lesson, is_published: !next });
      toast.error(error.message);
    }
  };

  const saveMeta = async () => {
    if (!lesson) return;
    setSavingMeta(true);
    const { error } = await supabase
      .from('course_lessons')
      .update({ title: titleEdit.trim() || lesson.title, description: descEdit || null })
      .eq('id', lesson.id);
    setSavingMeta(false);
    if (error) toast.error(error.message);
    else {
      toast.success('Saved');
      setLesson({ ...lesson, title: titleEdit.trim(), description: descEdit || null });
    }
  };

  const removeItem = async (item: PracticeItem) => {
    if (!confirm('Remove this practice item?')) return;
    const { error } = await supabase
      .from('lesson_practice_items')
      .delete()
      .eq('id', item.id);
    if (error) toast.error(error.message);
    else setItems((xs) => xs.filter((x) => x.id !== item.id));
  };

  if (loading || !lesson) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>{lesson.title} – Lesson editor</title>
        <meta name="description" content={`Edit lesson "${lesson.title}".`} />
      </Helmet>

      {course && (
        <Link
          to={`/org/courses/${course.id}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> {course.name}
        </Link>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Lesson details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="le-title">Title</Label>
            <Input
              id="le-title"
              value={titleEdit}
              onChange={(e) => setTitleEdit(e.target.value)}
              disabled={!canManage}
            />
          </div>
          <div>
            <Label htmlFor="le-desc">Description</Label>
            <Textarea
              id="le-desc"
              value={descEdit}
              onChange={(e) => setDescEdit(e.target.value)}
              disabled={!canManage}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="le-pub" className="text-sm">Published</Label>
              <Switch
                id="le-pub"
                checked={lesson.is_published}
                onCheckedChange={togglePublish}
                disabled={!canManage}
              />
            </div>
            {canManage && (
              <Button onClick={saveMeta} disabled={savingMeta} size="sm">
                {savingMeta ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Practice items</CardTitle>
          {canManage && (
            <Button size="sm" onClick={() => setPickerOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No practice items yet.
            </div>
          ) : (
            items.map((it, i) => {
              const kind = isPracticeKindId(it.kind_id)
                ? PRACTICE_KINDS[it.kind_id as PracticeKindId]
                : null;
              const Icon = kind?.icon;
              return (
                <div key={it.id} className="flex items-center gap-3 p-4">
                  <div className="text-xs text-muted-foreground w-6">{i + 1}</div>
                  {Icon && (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.label ?? 'Item'}</div>
                    <div className="text-xs text-muted-foreground">
                      {kind?.label ?? it.kind_id}
                    </div>
                  </div>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(it)}
                      aria-label="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {course && (
        <AddPracticeItemDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          lessonId={lesson.id}
          orgId={course.org_id}
          enabledKinds={enabledKinds}
          nextPosition={items.length}
          onAdded={load}
        />
      )}
    </div>
  );
}
